<template>
  <div class="bg-card rounded-lg shadow p-6 mt-6">
    <h2 class="text-xl font-semibold mb-4">Microsoft Integration</h2>
    <p class="text-muted-foreground mb-4">
      Connect your Microsoft account to enable access to SharePoint and other Microsoft services
    </p>

    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <div class="w-10 h-10 flex-shrink-0 mr-3">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" class="w-full h-full">
            <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
            <rect x="12" y="1" width="10" height="10" fill="#7fba00"/>
            <rect x="1" y="12" width="10" height="10" fill="#00a4ef"/>
            <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
          </svg>
        </div>
        <div>
          <p class="font-medium">
            <span v-if="isConnected">Microsoft Account Connected</span>
            <span v-else>Connect Microsoft Account</span>
          </p>
          <p v-if="isConnected" class="text-sm text-muted-foreground">
            Connected as {{ microsoftEmail || 'Unknown' }}
          </p>
          <p v-else class="text-sm text-muted-foreground">
            Enable SharePoint access and file management
          </p>
        </div>
      </div>

      <button
          @click="toggleMicrosoftConnection"
          :class="[
          'px-4 py-2 rounded-md transition-colors',
          isConnected
            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        ]"
          :disabled="loading || !userIdAvailable"
      >
        <div v-if="loading" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isConnected ? 'Disconnecting...' : 'Connecting...' }}</span>
        </div>
        <span v-else-if="!userIdAvailable">Loading user data...</span>
        <span v-else>{{ isConnected ? 'Disconnect' : 'Connect' }}</span>
      </button>
    </div>

    <div v-if="error" class="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
      {{ error }}
    </div>

    <div v-if="successMessage" class="mt-4 p-3 bg-green-100 text-green-700 rounded-md">
      {{ successMessage }}

      <!-- Neuer Button zum manuellen Aktualisieren der Seite (für Tab-basiertes Login) -->
      <div v-if="successMessage.includes('neuem Tab')" class="mt-2">
        <button
            @click="refreshStatus"
            class="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
        >
          Verbindungsstatus prüfen
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch, onUnmounted } from 'vue';

export default defineComponent({
  name: 'MicrosoftConnectCard',

  props: {
    userId: {
      type: String,
      required: true
    },
    userEmail: {
      type: String,
      required: true
    }
  },

  emits: ['microsoft-connected', 'microsoft-disconnected'],

  setup(props, { emit }) {
    const isConnected = ref(false);
    const microsoftEmail = ref<string | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const successMessage = ref<string | null>(null);
    const isFetchingStatus = ref(false);

    // Microsoft OAuth config
    const oauthConfig = {
      baseUrl: process.env.VUE_APP_O_AUTH_BASE_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/msauth',
      tokenApiUrl: process.env.VUE_APP_O_AUTH_API_TOKEN_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/usertoken'
    };

    // Computed property to check if user ID is available and valid
    const userIdAvailable = computed(() => {
      return !!props.userId && props.userId.length > 0;
    });

    // Watch for changes in user ID and email
    watch(() => [props.userId, props.userEmail], () => {
      if (userIdAvailable.value && !isFetchingStatus.value) {
        checkConnectionStatus();
      }
    });

    // Check if this is a callback from Microsoft OAuth
    onMounted(async () => {
      // Get connection status on mount if we have a userId
      if (userIdAvailable.value) {
        await checkConnectionStatus();
      }

      // Check if this is a callback from Microsoft OAuth
      const urlParams = new URLSearchParams(window.location.search);
      const isCallback = urlParams.get('ms_callback');
      const code = urlParams.get('code');

      if (isCallback && code) {
        if (!userIdAvailable.value) {
          error.value = "User ID not available. Cannot complete Microsoft authentication.";
          return;
        }

        await handleOAuthCallback(code);

        // Clean up URL parameters to avoid reprocessing on page refresh
        // Keep the current path (user-settings)
        const url = new URL(window.location.href);
        url.searchParams.delete('ms_callback');
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        url.searchParams.delete('endpoint');
        window.history.replaceState({}, document.title, url.toString());
      }
    });

    // Check if the user already has a Microsoft connection
    async function checkConnectionStatus() {
      if (!userIdAvailable.value) {
        console.log('Skipping connection check: No user ID available');
        return;
      }

      isFetchingStatus.value = true;

      try {
        console.log('Checking Microsoft connection status for user:', props.userId);

        // Use the usertoken API to check connection status
        const response = await fetch(`${oauthConfig.tokenApiUrl}?userId=${props.userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('Error checking connection status:', await response.text());
          return;
        }

        const data = await response.json();
        console.log('Connection status response:', data);

        const previousConnected = isConnected.value;
        isConnected.value = data.connected;
        microsoftEmail.value = data.microsoftEmail;

        // Wenn der Verbindungsstatus sich geändert hat und jetzt verbunden ist
        // (Wird verwendet, wenn ein Benutzer sich in einem anderen Tab angemeldet hat)
        if (!previousConnected && isConnected.value) {
          successMessage.value = 'Microsoft connection successful!';
          emit('microsoft-connected', {
            microsoftId: data.tokenId,
            microsoftEmail: microsoftEmail.value
          });
        }
      } catch (err) {
        console.error('Error checking Microsoft connection status:', err);
        // Don't show error to user for this check
      } finally {
        isFetchingStatus.value = false;
      }
    }

    // Handle the OAuth callback
    async function handleOAuthCallback(code: string) {
      loading.value = true;
      error.value = null;
      successMessage.value = null;

      try {
        console.log('Handling OAuth callback with code');

        // Exchange code for tokens via the Fission Function, passing userId in state
        const state = `cb_${props.userId}`;  // Use a prefix to identify it's a callback state

        // Exchange code for tokens via the Fission Function
        const response = await fetch(`${oauthConfig.baseUrl}?endpoint=callback&code=${code}&state=${state}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to complete Microsoft authentication');
        }

        const authData = await response.json();

        // If token is already stored by msauth function
        if (authData.message && authData.message.includes('token saved')) {
          console.log('Token already saved by msauth function');

          // Update the connection status
          isConnected.value = true;
          microsoftEmail.value = authData.user?.mail || authData.user?.userPrincipalName || '';

          successMessage.value = 'Microsoft account connected successfully!';
          emit('microsoft-connected', {
            microsoftId: authData.user?.id,
            microsoftEmail: microsoftEmail.value
          });

          return;
        }

        // If token is not stored yet, save it
        // Save token to database via usertoken API
        await saveTokenToDatabase(authData);

        isConnected.value = true;
        microsoftEmail.value = authData.user.mail || authData.user.userPrincipalName;

        successMessage.value = 'Microsoft account connected successfully!';
        emit('microsoft-connected', {
          microsoftId: authData.user.id,
          microsoftEmail: microsoftEmail.value
        });
      } catch (err) {
        console.error('Error handling OAuth callback:', err);
        error.value = err instanceof Error ? err.message : 'Failed to connect Microsoft account';
      } finally {
        loading.value = false;
      }
    }

    // Save token to database via API
    async function saveTokenToDatabase(authData: any) {
      const response = await fetch(oauthConfig.tokenApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: props.userId,
          email: props.userEmail,
          msId: authData.user.id,
          msEmail: authData.user.mail || authData.user.userPrincipalName,
          accessToken: authData.token_info.access_token,
          refreshToken: authData.token_info.refresh_token,
          expiresIn: authData.token_info.expires_in,
          tokenType: authData.token_info.token_type,
          scope: authData.token_info.scope
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save token');
      }

      return await response.json();
    }

    // Connect or disconnect Microsoft account
    function toggleMicrosoftConnection() {
      if (!userIdAvailable.value) {
        error.value = "User ID not available. Cannot proceed with Microsoft connection.";
        return;
      }

      if (isConnected.value) {
        disconnectMicrosoft();
      } else {
        connectMicrosoft();
      }
    }

    // Initiate Microsoft connection
    function connectMicrosoft() {
      if (!userIdAvailable.value) {
        error.value = "User ID not available. Cannot connect to Microsoft.";
        return;
      }

      // Reset any previous messages
      error.value = null;
      successMessage.value = null;
      loading.value = true;

      try {
        // Set state for CSRF protection and include userId
        const state = `${Math.random().toString(36).substring(2)}_${props.userId}`;
        sessionStorage.setItem('ms_auth_state', state);

        // Erstelle Login-URL
        let loginUrl = `${oauthConfig.baseUrl}?endpoint=login&state=${state}`;

        console.log('Opening Microsoft login in new tab:', loginUrl);

        // Öffne Microsoft Login in neuem Tab
        const newTab = window.open(loginUrl, '_blank');

        if (!newTab) {
          // Wenn Popup blockiert wurde
          error.value = "Popup was blocked. Please allow popups for this page and try again.";
          loading.value = false;
          return;
        }

        // Zeige Anleitung für den Benutzer
        successMessage.value = "Microsoft Login was opened in a new tab. Return here after logging in and refresh the page if needed."

        // Starte eine Überprüfung in regelmäßigen Abständen, ob die Verbindung hergestellt wurde
        startConnectionCheck();
      } catch (err) {
        console.error('Error opening Microsoft login:', err);
        error.value = err instanceof Error ? err.message : 'Error opening Microsoft Logins';
      } finally {
        loading.value = false;
      }
    }

    // Regelmäßige Überprüfung der Verbindung
    let connectionCheckInterval: number | null = null;

    function startConnectionCheck() {
      // Lösche vorherigen Interval falls vorhanden
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }

      // Prüfe alle 5 Sekunden, ob die Verbindung hergestellt wurde
      connectionCheckInterval = window.setInterval(async () => {
        if (!userIdAvailable.value) return;

        console.log('Checking if Microsoft connection was established...');
        const wasConnected = isConnected.value;

        await checkConnectionStatus();

        // Wenn die Verbindung neu hergestellt wurde
        if (!wasConnected && isConnected.value) {
          console.log('Microsoft connection was established!');
          clearInterval(connectionCheckInterval as number);
          connectionCheckInterval = null;

          // Aktualisiere Erfolgsmeldung
          successMessage.value = 'Microsoft connection was established!';

          // Benachrichtige übergeordnete Komponente
          emit('microsoft-connected', {
            microsoftId: 'unknown', // Wir kennen die ID nicht, ohne Auth-Daten
            microsoftEmail: microsoftEmail.value
          });
        }
      }, 5000);
    }

    // Bereinige Interval beim Unmounten der Komponente
    onUnmounted(() => {
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }
    });

    // Disconnect Microsoft account
    async function disconnectMicrosoft() {
      if (!userIdAvailable.value) {
        error.value = "User ID not available. Cannot disconnect from Microsoft.";
        return;
      }

      loading.value = true;
      error.value = null;
      successMessage.value = null;

      try {
        console.log('Disconnecting Microsoft account for user:', props.userId);

        // Use the usertoken API to disconnect the account
        const response = await fetch(`${oauthConfig.tokenApiUrl}?userId=${props.userId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to disconnect Microsoft account');
        }

        isConnected.value = false;
        microsoftEmail.value = null;
        successMessage.value = 'Microsoft account disconnected successfully!';
        emit('microsoft-disconnected');
      } catch (err) {
        console.error('Error disconnecting Microsoft account:', err);
        error.value = err instanceof Error ? err.message : 'Failed to disconnect Microsoft account';
      } finally {
        loading.value = false;
      }
    }

    return {
      isConnected,
      microsoftEmail,
      loading,
      error,
      successMessage,
      toggleMicrosoftConnection,
      userIdAvailable,
      refreshStatus: checkConnectionStatus // Manuelles Aktualisieren des Status
    };
  }
});
</script>
